import { doc, increment, setDoc, updateDoc } from 'firebase/firestore'
import React, { useState } from 'react'

import { Button } from '@components/mantine/button.component'
import { Modal } from '@components/modal.component'
import { Required } from '@components/required.component'
import { Select } from '@components/mantine/select.component'
import { useAppState } from '@src/app.state'
import { db } from '@src/firebase-app'
import { dbNames } from '@utils/constants'
import { getTodayDate } from '@utils/helpers'
import { toastService } from '@utils/toast.service'

type AdoptTemplateModalProps = {
  onReject: () => void
  onResolve: () => void
  template: MarketplaceTemplate & { description: string }
}

export function AdoptTemplateModal({ onReject, onResolve, template }: AdoptTemplateModalProps) {
  const [importing, setImporting] = useState(false)
  const user = useAppState(state => state.user)
  const orgs = useAppState(state => state.orgs)
  const adminOrgs = orgs.filter(({ id }) =>
    ['owner', 'manager', 'editor'].includes(user.orgRoles[id]),
  )
  const [selectedOrg, setSelectedOrg] = useState<Organization | undefined>(adminOrgs[0])
  const orgOptions = adminOrgs.map(org => ({ value: org.id, label: org.name }))

  async function handleImport() {
    setImporting(true)
    const today = getTodayDate().toISO()
    const importTemplate = {
      name: template.name,
      description: template.description,
      orgId: selectedOrg.id,
      marketplaceTemplateId: template.id,
      createdDate: today,
      authorOrgId: template.orgId,
    }

    try {
      await setDoc(
        doc(
          db,
          dbNames.adoptedMarketplaceTemplates,
          `${selectedOrg.id}_${template.id}`,
        ),
        importTemplate,
      )
      await updateDoc(doc(db, dbNames.marketplaceTemplates, template.id), {
        adoptionsCount: increment(1),
      })
      toastService.info(`Template imported to ${selectedOrg.name} successfully`)
      onResolve()
    } catch (err) {
      console.log(err)
      setImporting(false)
      toastService.error(
        `There was a problem importing this template. Please try again another time`,
      )
    }
  }

  return (
    <>
      <Modal.Header>Import to workspace</Modal.Header>
      <Modal.Body>
        <div className="row break-1">
          <div className="col-12">
            <div className="form-group">
              <label htmlFor="select-workspace">
                Select an import destination <Required />
              </label>
              <Select
                data={orgOptions}
                placeholder="Select workspace"
                fullWidth
                onChange={(value: string | null) =>
                  setSelectedOrg(adminOrgs.find(org => org.id === value))
                }
                value={selectedOrg?.id}
              />
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="tertiary"
          onClick={onReject}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleImport}
          loading={importing}
        >
          Import
        </Button>
      </Modal.Footer>
    </>
  )
}
